const content = `<p>To be efficient in reducing coupling, abstractions need to hide from their callers the details of their implementation. Hiding implementation details is called encapsulation.</p>

<p>But some abstractions are flawed and don’t provide good encapsulation, sometimes so much so that they end up adding coupling instead of removing it.</p>

<p>The 3 following anti-patterns are common pitfalls of abstraction design:</p>

<ul><li>leaky abstractions,</li><li>bureaucratic abstractions,</li><li>premature abstractions.</li></ul>

<p>Here is what they mean, why they can create coupling and how to avoid them.</p>

<h2 id="Leaky-abstractions">Leaky abstractions</h2>

<p>The purpose of introducing a level of abstraction with classes and functions is to reduce coupling between two concerned parts of the code: the code that calls the class and the code that is inside the class.</p>

<p>The abstraction is leaky if the caller still has to know things about the inside of the class or function.</p>

<p>Let’s take an example to illustrate. The following class allows to input scores:</p>

<pre><code class="language-ruby">class Scores
  def initialize
    @file = File.open 'scores.txt', 'a'
  end

  def add(score)
    @file.write score.to_s + "\n"
  end

  def finish
    @file.close
  end
end</code></pre>

<p>The class stores the scores in a file, but that’s an implementation detail that the user of <code>Scores</code> is not concerned about. The class decouples its caller from the code handling the file.</p>

<p>Here is how the class is used:</p>

<pre><code class="language-ruby">scores = Scores.new
scores.add 40
scores.add 30
scores.finish</code></pre>

<p>However, from the caller's perspective the last line does not make sense. Why does it have to call <code>scores.finish</code>?</p>

<p>It’s because someone has to close the file at some point. The class encapsulates most of the details about file handling, but the aspect of closing the file still shows outside of its interface and leaves some coupling with the caller.</p>

<p>Beyond adding a line of unclear code, this creates the risk for some callers (including those that aren’t written yet) never to close the file.</p>

<p>Also, it creates a risk of calling the methods in the wrong order: if a user calls <code>add</code> after having called <code>finish</code> then the <code>Score</code> class doesn't behave correctly as it accesses a closed file.</p>

<p>One way to detect such leaky abstractions is to make the following thought experiment: if the implementation was different, would that change the interface? If so, it suggests that the abstraction is leaky. In our case, another implementation such as storing scores in memory or sending them over HTTP may not need a <code>finish</code> method for example.</p>

<h3 id="How-to-fix">How to fix</h3>

<p>The general idea to fix leaky abstractions is to make the interface the least surprising to its user possible. It’s not always easy nor possible, but often leaky interfaces can be improved.</p>

<p>In our example, the surprising part is the need for the user to call <code>finish</code>. We can turn around the flow of the class by leaving some space for the user to add their scores, and keep control on what happens before and after:</p>

<pre><code class="language-ruby">class Scores
  def self.enter_scores
    file = File.open 'scores.txt', 'a'
    scores = Scores.new file
    yield scores
    file.close
  end

  def add(score)
    @file.write score.to_s + "\n"
  end

  private

  def initialize(file)
    @file = file
  end
end</code></pre>

<p>The calling code now looks like this:</p>

<pre><code class="language-ruby">Scores.enter_scores do |scores|
  scores.add 40
  scores.add 30
end</code></pre>

<p>The caller no longer has to call <code>finish</code> and the fact that <code>Scores</code> uses a file no longer appears in the interface. The private <code>initialize</code> forces the user to call the new function <code>enter_scores</code>.</p>

<p>There are many <a href="https://www.joelonsoftware.com/2002/11/11/the-law-of-leaky-abstractions/">other types of leaky abstractions</a>, such as inefficient performance when using the interface in a certain way due to the implementation.</p>

<h2 id="Bureaucratic-abstractions">Bureaucratic abstractions</h2>

<p>A bureaucratic abstraction is useless because it hides nothing:</p>

<pre><code class="language-ruby">def valid?(transaction)
  transaction.valid?
end</code></pre>

<p>Introducing levels of abstraction is useful to hide lower level details, but when a function is at the same level of abstraction as its implementation, it doesn’t carry its own weight.</p>

<p>Note that this doesn’t mean that one-line functions are always useless. Some can increase the level of abstraction by reformulating a lower level concern, thus hiding implementation details:</p>

<pre><code class="language-ruby">def valid?(transaction)
  transaction.paid?
end</code></pre>

<p>In a certain context, transactions can be considered valid when they’ve been paid. Being paid is <strong>How</strong> to determine if a transaction is valid, so it’s a lower level of abstraction, and in this context this function is justified to increase the level of abstraction and hide this detail.</p>

<p>Another typical use of one-line functions are those that hide the code that we can’t control, such as library calls. Indeed libraries' interfaces, or the libraries themselves, may change. Having an abstraction over library calls prevent those changes to spread in the code.</p>

<h3 id="How-to-fix.1">How to fix</h3>

<p>Either make the abstraction better at hiding concerns, or remove it altogether.</p>

<h2 id="Premature-abstractions">Premature abstractions</h2>

<p>Premature abstraction happen when we rush into introducing abstractions before having enough understanding of the code.</p>

<p>Let’s take the example of a chess game to illustrate. We write a function to determine if a given move on the board is allowed by the chess rules.</p>

<p>We start by implementing the case of a pawn: if the destination cell is empty, a pawn can move up one cell vertically, or two cells if it’s on its initial position (line 1), and if an enemy piece occupies the destination cell, the pawn can move up on cell diagonally.</p>

<p>Here is one way to implement that (<code>from</code> and <code>to</code> are the start and destination cells, and <code>board.content(cell)</code> provides the piece on cell <code>cell</code> (or <code>nil</code> if it is empty)):</p>

<pre><code class="language-ruby">allowed_move = if (to.y - from.y) == 1 &amp;&amp; to.x == from.x
  board.content(to).nil?
elsif (to.y - from.y) == 2 &amp;&amp; to.x == from.x
  board.content(to).nil? &amp;&amp; from.y == 1
elsif (to.y - from.y) == 1 &amp;&amp; (to.x - from.x).abs == 1
  !board.content(to).nil? &amp;&amp; board.content(to).color != piece.color
else
  false
end</code></pre>

<p>That’s a lot of <code>x</code>s and <code>y</code> that look like low-level concerns. We may itch for introducing levels of abstraction in this code.</p>

<p>One way to do it is this:</p>

<pre><code class="language-ruby">allowed_move = if one_cell_up(from, to)
  not_occupied(board, to)
elsif two_cells_up(from, to)
  not_occupied(board, to) &amp;&amp; at_initial_position?(from)
elsif one_cells_up_diagonal(from, to)
  occupied_by_enemy(board, to, piece)
else
  false
end</code></pre>

<p>This code introduces abstractions that hide most of the details. But they have the following drawbacks:</p>

<ul><li>some of them are very specific to pawns (like <code>two_cells_up</code>), all other pieces will have to implement their own little functions</li><li>they make us blind to the concepts that could be common to all pieces</li><li>they can’t accommodate unanticipated changes (4 player-chess for example)</li></ul>

<p>These are premature abstractions, as we don’t have enough understanding of how to represent chess rules in code at this stage. By locking the code into this early understanding, they create coupling by making the code hard to change if we need to change concepts that are common to all pieces.</p>

<h3 id="How-to-fix.2">How to fix</h3>

<p>Tolerate the low level details when there is not too much code yet, and create abstractions when they become more obvious. If there were only pawns in the game, that would mean to leave the code as it was without introducing abstractions.</p>

<p>To continue implementing a complete chess game, the process would then be:</p>

<ul><li>implement the rules of rooks in a branch of case statement</li><li>compare the two, refactor them so that some of their parts become identical</li><li>introduce abstractions to factor out the identical pieces</li><li>implement the rules of bishop in a branch of case statement</li><li>compare with the other two, refactor them so that some of their parts become identical</li><li>introduce abstractions to factor out the identical pieces</li><li>and so on.</li></ul>

<p>Following this process the code can look like this:</p>

<pre><code class="language-ruby">def authorized_move?(piece_type, move, board)
  case piece_type
  when :pawn
    return false unless move.forward?
    move_to_enemy_piece = !board.content(move.to).nil? &amp;&amp; board.content(move.to).color != board.content(move.from).color
    (move.vertical? &amp;&amp; (move.distance == 1 || move.distance == 2 &amp;&amp; move.from.y == 1)) ||
    (move.diagonal? &amp;&amp; move.distance == 1 &amp;&amp; move_to_enemy_piece)
  when :rook
    move.horizontal? || move.vertical?
  when :bishop
    move.diagonal?
  when :king
    (move.horizontal? || move.vertical? || move.diagonal?) &amp;&amp; move.distance == 1
  when :queen
    move.horizontal? || move.vertical? || move.diagonal?
  when :knight
    move.l?
  end
end</code></pre>

<p>The successive refactoring steps between the discoveries of the concepts of the rules lead to abstractions that are common to all pieces and hide the lower levels of the code.</p>

<div class="notes">
<p>Key points:</p>

<ul><li>Leaky abstractions make their caller bear some aspect of their implementations</li><li>Make your abstractions less leaky by removing coupling to implementation details</li><li>Bureaucratic abstractions add overhead and hide little</li><li>Make their encapsulation better or remove them altogether</li><li>Premature abstractions are a temptation to hide details too early</li><li>Introduce abstractions little by little along with your understanding of the code</li></ul>
</div>`

export default content
